// =============================================================================
// domain/processMetrics.ts — did you follow your own rules, week by week?
// -----------------------------------------------------------------------------
// Two weekly series over the same buckets:
//
//   adherence   the SHARE of that week's reviewed trades where a rule held
//   mistakes    the COUNT of flagged breaches that week
//
// ── 🚨 P&L CAN RISE WHILE ADHERENCE FALLS, AND THAT IS THE WHOLE POINT ──
//
// A good week traded badly is the most dangerous week a trader has, because the
// result rewards the process that produced it. Two charts stacked is how you see
// that: the money above, the discipline below.
//
// ── 🚨 AN UNREVIEWED TRADE IS NOT A CLEAN ONE ──
//
// The breach flags are stored as "absent means it did not happen" — an unchecked
// box is DELETED, never written false. So a freshly imported trade has every
// flag absent, and counting it as clean would push a week of 40 untouched
// imports to 100% adherence. Reviewed trades only, and a week with none reports
// **null** rather than a number.
//
// ⚠ THE OLD CLIENT WRITES `adherenceRate(...) ?? 0`, so a week where nothing was
// reviewed plots at ZERO — a flat line along the floor that reads as total
// indiscipline on a week the trader simply has not annotated yet. PD-21.
//
// ── ⚠ THE FLAGS ARE NEGATIVES, AND THE SERIES ARE POSITIVES ──
//
// What is stored is `planNotFollowed`, `stopNotHonored`, `subAPlus`. What is
// plotted is "plan followed", "stop honored", "A+ setup" — each is the SHARE NOT
// FLAGGED. Getting that backwards inverts every line on the chart, and it would
// still look like a plausible chart.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

export interface ProcessTrade {
  /** The EFFECTIVE date — resolved by the caller. */
  date: string | null;
  /** ⚠ `false` means never reviewed. Absent/true both mean reviewed. */
  annotated: boolean | null | undefined;
  booleans: Record<string, unknown> | null | undefined;
  /** Free-form mistake tags, counted rather than named. */
  mistakeTags: readonly string[] | null | undefined;
}

/** The four adherence lines, each the share of trades NOT carrying its flag. */
export const ADHERENCE_SERIES = [
  { key: 'plan', flag: 'planNotFollowed', label: 'Plan followed' },
  { key: 'aPlus', flag: 'subAPlus', label: 'A+ setup' },
  { key: 'stop', flag: 'stopNotHonored', label: 'Stop honored' },
  { key: 'target', flag: 'targetNotHonored', label: 'Target honored' },
] as const;

/** The four mistake lines, each a raw count. */
export const MISTAKE_SERIES = [
  { key: 'revenge', flag: 'revengeTrade', label: 'Revenge trade' },
  { key: 'fomo', flag: 'fomoTrade', label: 'FOMO trade' },
  { key: 'overtraded', flag: 'overtraded', label: 'Overtraded' },
  { key: 'tags', flag: null, label: 'Mistake tags' },
] as const;

export interface ProcessWeek {
  /** ISO date of the week's Sunday — the old client's week start. */
  weekStart: string;
  label: string;
  trades: number;
  /** Of those, how many were reviewed. The denominator for every share. */
  reviewed: number;
  /** ⚠ NULL where nothing was reviewed that week, never 0. */
  adherence: Record<string, number | null>;
  mistakes: Record<string, number>;
}

/**
 * The share of a week's REVIEWED trades where the flag was not set.
 *
 * ⚠ NULL, NOT ZERO, when nothing was reviewed. "Nobody followed the plan" and
 * "nobody has looked at these yet" are opposite readings, and only the first is
 * about the trader.
 */
export function adherenceRate(
  trades: readonly ProcessTrade[], flag: string,
): number | null {
  const reviewed = trades.filter((t) => t.annotated !== false);
  if (!reviewed.length) return null;
  const clean = reviewed.filter((t) => t.booleans?.[flag] !== true).length;
  return Math.round((100 * clean) / reviewed.length);
}

// ── 🚨 THE CLIENT HAS TWO WEEK RULES, ON PURPOSE, BECAUSE BETA HAS TWO ──
//
// `weekStartOf` is SUNDAY and matches beta's `startOfWeek` (app/index.html:7190):
// the Process card, the weekly Review and the goals all take it.
// `weekStartMonday` is MONDAY and matches beta's `groupTradesByPeriod`
// (app/index.html:42592, whose own header reads "day/week(Mon)/month"): every
// analytics chart that buckets by week takes it.
//
// 🔴 THEY ARE ADJACENT BECAUSE ONE OF THEM USED TO LOOK LIKE THE ONLY ONE. Two
// panels on Custom Analytics were handed the Sunday rule under a comment that
// read "the client's ONE week rule — Monday", so the two ready-made dashboards
// bucketed from Sunday while the chart builder beside them bucketed from Monday
// — one book, three weekly trends, and every bar labelled a day earlier than
// beta's. A wrong comment beside a right function is how that happens; two
// named functions under one header is the fix.
//
// ⚠ Neither takes a day-boundary mode. The caller resolves the DATE first (the
// session day, not the calendar one) and these only bucket what they are given.

/** The Sunday of that date's week, as ISO. ⚠ Local midnight, as everywhere. */
export function weekStartOf(iso: string): string | null {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - d.getDay());
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** The MONDAY of that date's week, as ISO — beta's analytics-chart rule. */
export function weekStartMonday(iso: string): string | null {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `Jul 13` — the same short form the old client's axis uses. */
export function weekLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * One bucket per week that has a trade, in date order.
 *
 * ⚠ WEEKS WITH NO TRADES ARE ABSENT, not zero-filled. Zero-filling a fortnight
 * off would draw two weeks of "no mistakes and no adherence" — a stretch of
 * apparently perfect discipline over a holiday.
 */
export function processMetrics(trades: readonly ProcessTrade[]): ProcessWeek[] {
  const byWeek = new Map<string, ProcessTrade[]>();
  for (const t of trades) {
    if (!t.date) continue;
    const ws = weekStartOf(t.date);
    if (!ws) continue;
    const arr = byWeek.get(ws) ?? [];
    arr.push(t);
    byWeek.set(ws, arr);
  }

  return [...byWeek.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([weekStart, week]) => {
      const adherence: Record<string, number | null> = {};
      for (const s of ADHERENCE_SERIES) adherence[s.key] = adherenceRate(week, s.flag);

      const mistakes: Record<string, number> = {};
      for (const s of MISTAKE_SERIES) {
        mistakes[s.key] = s.flag == null
          // ⚠ The TOTAL number of tags, not the number of trades carrying one —
          // a trade tagged three ways is three mistakes on this line, which is
          // the old client's meaning and the reason the series is called
          // "mistake tags" rather than "mistagged trades".
          ? week.reduce((n, t) => n + (t.mistakeTags?.length ?? 0), 0)
          : week.filter((t) => t.booleans?.[s.flag as string] === true).length;
      }

      return {
        weekStart,
        label: weekLabel(weekStart),
        trades: week.length,
        reviewed: week.filter((t) => t.annotated !== false).length,
        adherence,
        mistakes,
      };
    });
}
